import { useAchievementTypes, useReduxState } from 'hooks';
import { AchievementIcon } from 'components/achievements';
import {
  ContentLabel,
  ContentValue,
  FlexContainer,
  Link,
  RichTextValue,
  User
} from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import { DisplayAttributeInProductPage } from 'components/common/customAttributes';
import { CompetenciesProductBadge } from 'components/competencies';

import type {
  CompetencyInProduct,
  Achievement,
  User as UserType,
  UserWithBasicData
} from 'shared-types';

interface Props {
  achievement: Pick<
    Achievement,
    'customAttributes' | 'description' | 'id' | 'title' | 'type'
  > & { competencies: CompetencyInProduct[]; owners: UserWithBasicData[] };
  isUserPage: boolean;
  loading: boolean;
  targetUser: Pick<UserType, 'id'>;
}

const ProfileAchievementsAchievementInfo = ({
  achievement,
  isUserPage,
  loading
}: Props) => {
  const { messages, theme } = useReduxState();

  const achievementTypes = useAchievementTypes();

  return (
    <div className="summary">
      <Box
        loading={loading}
        title={
          <BoxBackLink
            simpleReturn={true}
            text={messages.commonAllAchievements}
          />
        }
      >
        {achievement ? (
          <div className="inner">
            <div className="icon">
              <AchievementIcon type={achievement.type} />
            </div>

            <div className="type">
              {achievementTypes[achievement.type].title}
            </div>

            <div className="title">
              <Link
                href={
                  isUserPage
                    ? `/admin/achievements/${achievement.id}`
                    : `/achievements/${achievement.id}`
                }
                linkColor="inherit"
                style={{ display: 'inline-block' }}
              >
                <div className="title-text">{achievement.title}</div>
              </Link>
            </div>

            {achievement.description ? (
              <div className="description">
                <RichTextValue html={achievement.description} />
              </div>
            ) : null}

            <div className="details">
              <DisplayAttributeInProductPage
                customAttributes={achievement.customAttributes}
                inProfile={true}
                labelSpacing={30}
                position="at start"
                productType={achievement.type}
              />

              <ContentLabel label={messages.commonOwners} spacing={30}>
                <ContentValue>
                  <div className="owners">
                    {achievement.owners.map((owner) => (
                      <User
                        key={owner.id}
                        size="small"
                        showEmail={false}
                        showEmailTooltip={false}
                        titleStyle={{ fontSize: '1.4rem' }}
                        user={owner}
                      />
                    ))}
                  </div>
                </ContentValue>
              </ContentLabel>

              {achievement.competencies?.length ? (
                <ContentLabel
                  label={messages.competenciesGranted}
                  spacing={30}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <ContentValue>
                    <FlexContainer style={{ flexWrap: 'wrap', gap: 10 }}>
                      {achievement.competencies.map((competency) => (
                        <CompetenciesProductBadge
                          key={competency.id}
                          competency={competency}
                        />
                      ))}
                    </FlexContainer>
                  </ContentValue>
                </ContentLabel>
              ) : null}

              <DisplayAttributeInProductPage
                customAttributes={achievement.customAttributes}
                inProfile={true}
                labelSpacing={30}
                productType={achievement.type}
              />
            </div>
          </div>
        ) : null}
      </Box>

      <style jsx>{`
        .info {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 600;
        }
        .type {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 600;
          margin-top: 10px;
        }
        .title {
          margin-top: 5px;
        }
        .title-text {
          color: ${theme.textColor};
          font-size: 1.8rem;
          font-weight: 700;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          line-height: 1.5;
          margin-top: 15px;
        }
        .details {
          margin-top: 30px;
        }
        .owners {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .categories {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
      `}</style>
    </div>
  );
};

export default ProfileAchievementsAchievementInfo;
